import * as Sentry from "@sentry/react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Outlet, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import useGetProfile from "../../hooks/use-get-profile";
import { finishWebView, getPrefsBoolean, getUserData } from "../../shared/appInterface";
import { APP_INTERACTION_TYPES, IS_R2R_USER_SESSION_KEY, KEY_USER_ID } from "../../shared/constants";
import useUpdateCurrentRouteOnServer from "../../shared/hooks/use-update-current-route-on-server";
import { useOnboardingStore } from "../../shared/store";
import { updateUserProfile } from "../../shared/utils/helpers";
import FullScreenLoader from "../loader/full-screen-loader";
import { useQuery } from "@tanstack/react-query";
import { httpClient } from "../../shared/http-client";
import { GET_CONFIGURATION, GET_USER_PREFERENCES } from "../../shared/constants/URLS";
import { useRefreshTokenStore } from "../../shared/useRefreshTokenStore";
import { useWebQueryParamStore } from "../../shared/stores/query-param-store";
import { userCreatedOrNotStore } from "../../shared/stores/resume-parsed-store";

const RootLayout = () => {
    const [tokenAdded, setTokenAdded] = useState(false);
    const [searchParams] = useSearchParams();
    const { i18n } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { getLastVisitedRoute, status: lastVisitedFetchStatus } =
        useUpdateCurrentRouteOnServer();
    const isCommunityOnboardingEnabled = useWebQueryParamStore((state) => state.isCommunityOnboardingEnabled);
    const setJobTypePreferences = useOnboardingStore(
        (state) => state.setJobTypesPreferences,
    );
    const setConfig = useOnboardingStore(state => state.setConfig)
    const setPreferredRoles = useOnboardingStore(
        (state) => state.setPreferredRoles,
    );
    const setTokenRefreshed = useRefreshTokenStore((state) => state.setTokenRefreshed);
    const setTokenRefreshing = useRefreshTokenStore((state) => state.setTokenRefreshing);
    const isResumeParsingFlow = useOnboardingStore.getState().isResumeParsingFlow;
    const { setCandidateToEnriched} = userCreatedOrNotStore((state) => state);

    useQuery(
        ["KEY_PREF"],
        () => {
            return httpClient.get(GET_USER_PREFERENCES).then((res) => res.data);
        },
        {
            onSuccess: (data) => {
                const lang = data?.language_preference?.type ?? "en";
                setJobTypePreferences(data?.job_types || []);
                setPreferredRoles(data?.job_types_v2 || []);
            },
            retry: 1,
        },
    );

    const {isLoading: isConfigLoading } = useQuery(["app-config"], () => {
        return httpClient.get(GET_CONFIGURATION).then((res) => res.data);
    }, {
        enabled: tokenAdded,
        retry: 1,
        onSuccess: (data) => {
            setConfig(data);
        }
    })

    useEffect(() => {
        /**
         * Register callback event for token refreshment.
         */
        const handleAccessToken = (e: any) => {
            if (e.detail.accessToken) {
                sessionStorage.setItem("__token__", e.detail.accessToken);
                /**
                 * Set global status of token refreshment respectively.
                 */
                setTokenRefreshed(true);
                setTokenRefreshing(false);
            } else {
                /**
                 * In case we don't receive a token, we set the states to default values
                 * so that when another 401 is triggered we initiate the flow again.
                 */
                setTokenRefreshing(false);
            }
        };
        window.addEventListener(APP_INTERACTION_TYPES.ACCESS_TOKEN, handleAccessToken);
        return () =>
            window.removeEventListener(
                APP_INTERACTION_TYPES.ACCESS_TOKEN,
                handleAccessToken,
            );
    }, []);

    const shouldGetLastVisitedRoute = () => {
        /* These are independent routes that can be triggered via Android independently
        * We should not get last visited route in case an independent route is opened,
        * and instead open that independent route */
        return ![
            "/community-onboarding/",
            "/community-onboarding/1",
            "/recruiter-actions/hr-report-card",
            "/recruiter-actions/search-appearances",
            "/recruiter-actions/hr-views",
            "/recruiter-actions/hr-shortlists",
            "/email",
            "/email/verify",

        ].includes(location.pathname) &&
        !isCommunityOnboardingEnabled &&
        !location.pathname.includes("/missing-entity/tiger-eye")
    };

    useEffect(() => {
        
        if (shouldGetLastVisitedRoute())
            getLastVisitedRoute();

        if (searchParams.has("client") && searchParams.get("client") === "android") {
            const userData = getUserData();
            const isNewUserOnboarding = getPrefsBoolean("PREF_IS_PROFILE_COMPLETE");
            const token = userData.accessToken;
            const userLang = userData.language;

            sessionStorage.setItem("__token__", token);
            sessionStorage.setItem("__userLang__", userLang);
            sessionStorage.setItem(IS_R2R_USER_SESSION_KEY, isNewUserOnboarding);
            updateUserProfile({ user_language: userLang });
            i18n.changeLanguage(userLang);
            if (!token) {
                // TODO: improve handling
                finishWebView();
            }
            setTokenAdded(true);
        } else {
            i18n.changeLanguage(sessionStorage.getItem("__userLang__") || "en");
            // sessionStorage.setItem(IS_R2R_USER_SESSION_KEY, "true");
            setTokenAdded(true);
        }
    }, []);

    const setFetchedProfile = useOnboardingStore((state) => state.setFetchedProfile);
    const { fetchStatus } = useGetProfile({
        onSuccess: (profile) => {
            sessionStorage.setItem(KEY_USER_ID, profile.user_id);
            if(!isResumeParsingFlow){
                if(!!profile.user_id){
                    setCandidateToEnriched(true);
                }
                setFetchedProfile(profile);
            }
        },
        refetchOnWindowFocus: false,
        cacheTime: Infinity,
        enabled: tokenAdded,
        retry: false,
    });

    const isLoadingLastVisitedRoute = lastVisitedFetchStatus === "loading";
    const isLoadingEnrichedProfile = fetchStatus === "fetching";
    return (
        <Sentry.ErrorBoundary fallback={<></>}>
            <div>
                {isLoadingEnrichedProfile || isLoadingLastVisitedRoute || !tokenAdded || isConfigLoading ? (
                    <FullScreenLoader />
                ) : (
                    <Outlet />
                )}
                <ToastContainer
                    position="top-right"
                    autoClose={3000}
                    hideProgressBar={false}
                    newestOnTop
                    closeOnClick
                    rtl={false}
                    pauseOnFocusLoss={false}
                    draggable={false}
                    pauseOnHover
                    theme="colored"
                />
            </div>
        </Sentry.ErrorBoundary>
    );
};

export default RootLayout;
